import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { ChartBarIcon, CalendarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  anio?: number;
  tipoEvaluacion: string;
  cantidadTemas: number;
}

interface EstadisticasPorFecha {
  fecha: string;
  tipoFecha: string | null;
  descripcionFecha: string | null;
  totalNotas: number;
  aprobados: number;
  desaprobados: number;
  temasEnBlanco: number;
  temasEnBlancoPorTema: { [key: number]: number };
  distribucionNotas: { [key: string]: number };
  distribucionPorTema?: { [key: number]: { A: number; D: number; DU: number; B: number } };
  porcentajeAprobados: string;
}

interface FechaExamen {
  id: number;
  fecha: string;
  tipo: string;
  descripcion: string | null;
  activa: boolean;
}

interface EstadisticasAprobados {
  curso: Curso;
  totalInscritos: number;
  totalAprobados: number;
  totalDesaprobados: number;
  estadisticasPorFecha: EstadisticasPorFecha[];
  primeraFecha: EstadisticasPorFecha | null;
  segundaFecha: EstadisticasPorFecha | null;
  fechasExamen: FechaExamen[];
}

const ListadoNotasPage: React.FC = () => {
  const [selectedCurso, setSelectedCurso] = useState<number | null>(null);

  // Fetch cursos
  const { data: cursos, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos'],
    queryFn: async () => {
      try {
        const response = await axios.get(`/cursos`);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching cursos:', error);
        return [];
      }
    },
  });

  // Fetch estadísticas
  const { data: estadisticas, isLoading: loadingEstadisticas } = useQuery<EstadisticasAprobados>({
    queryKey: ['estadisticas-aprobados', selectedCurso],
    queryFn: async () => {
      if (!selectedCurso) return null;
      try {
        const response = await axios.get(`/notas/curso/${selectedCurso}/estadisticas-aprobados`);
        return response.data;
      } catch (error) {
        console.error('Error fetching estadísticas:', error);
        return null;
      }
    },
    enabled: !!selectedCurso,
  });

  const getTipoFechaLabel = (tipo: string | null) => {
    if (!tipo) return 'Sin tipo';
    const tipos: { [key: string]: string } = {
      'primera_fecha': 'Primera Fecha',
      'recuperatorio_1': 'Recuperatorio 1',
      'recuperatorio_2': 'Recuperatorio 2',
      'fecha_extra': 'Fecha Extra',
    };
    return tipos[tipo] || tipo;
  };

  if (loadingCursos) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <ChartBarIcon className="h-8 w-8 mr-3 text-blue-600" />
          Dashboard de Estadísticas del Curso
        </h1>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar Curso
          </label>
          <select
            value={selectedCurso || ''}
            onChange={(e) => setSelectedCurso(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Seleccionar curso</option>
            {Array.isArray(cursos) && cursos.map((curso: Curso) => (
              <option key={curso.id} value={curso.id}>
                {formatCursoLabel(curso)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCurso && (
        <>
          {loadingEstadisticas ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando estadísticas...</p>
              </div>
            </div>
          ) : estadisticas ? (
            <>
              {/* Resumen general */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
                  Resumen General - {formatCursoNombreAnio(estadisticas.curso)}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="text-sm font-medium text-blue-600">Total Inscritos</div>
                    <div className="text-3xl font-bold text-blue-900 mt-2">
                      {estadisticas.totalInscritos}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="text-sm font-medium text-green-600">Total Aprobados</div>
                    <div className="text-3xl font-bold text-green-900 mt-2">
                      {estadisticas.totalAprobados}
                    </div>
                    <div className="text-sm text-green-700 mt-1">
                      {estadisticas.totalInscritos > 0 
                        ? `${((estadisticas.totalAprobados / estadisticas.totalInscritos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <div className="text-sm font-medium text-red-600">Total Desaprobados</div>
                    <div className="text-3xl font-bold text-red-900 mt-2">
                      {estadisticas.totalDesaprobados}
                    </div>
                    <div className="text-sm text-red-700 mt-1">
                      {estadisticas.totalInscritos > 0 
                        ? `${((estadisticas.totalDesaprobados / estadisticas.totalInscritos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                </div>

                {/* Información del curso */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Código:</span>
                      <span className="ml-2 text-gray-900">{estadisticas.curso.codigo}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Tipo Evaluación:</span>
                      <span className="ml-2 text-gray-900">
                        {estadisticas.curso.tipoEvaluacion === 'nota_por_tema' ? 'Por Tema' : 'Nota Completa'}
                      </span>
                    </div>
                    {estadisticas.curso.tipoEvaluacion === 'nota_por_tema' && (
                      <div>
                        <span className="font-medium text-gray-700">Cantidad de Temas:</span>
                        <span className="ml-2 text-gray-900">{estadisticas.curso.cantidadTemas}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Comparativa Primera y Segunda Fecha */}
              {(estadisticas.primeraFecha || estadisticas.segundaFecha) && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <CalendarIcon className="h-6 w-6 mr-2 text-purple-600" />
                    Comparativa de Parciales
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Primera Fecha */}
                    {estadisticas.primeraFecha && (
                      <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3">
                          Primera Fecha
                          {estadisticas.primeraFecha.descripcionFecha && (
                            <span className="text-sm font-normal text-blue-700 ml-2">
                              ({estadisticas.primeraFecha.descripcionFecha})
                            </span>
                          )}
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-700">Fecha:</span>
                            <span className="font-medium text-gray-900">
                              {new Date(estadisticas.primeraFecha.fecha).toLocaleDateString('es-AR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Total Notas:</span>
                            <span className="font-semibold text-gray-900">{estadisticas.primeraFecha.totalNotas}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Aprobados:</span>
                            <span className="font-semibold text-green-600">{estadisticas.primeraFecha.aprobados}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Desaprobados:</span>
                            <span className="font-semibold text-red-600">{estadisticas.primeraFecha.desaprobados}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">% Aprobados:</span>
                            <span className="font-semibold text-gray-900">{estadisticas.primeraFecha.porcentajeAprobados}%</span>
                          </div>
                          {estadisticas.curso.tipoEvaluacion === 'nota_por_tema' && (
                            <div className="mt-3 pt-3 border-t border-blue-300">
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-700 font-medium">Temas en Blanco:</span>
                                <span className="font-semibold text-orange-600">{estadisticas.primeraFecha.temasEnBlanco}</span>
                              </div>
                              {Object.keys(estadisticas.primeraFecha.temasEnBlancoPorTema).length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {Object.entries(estadisticas.primeraFecha.temasEnBlancoPorTema).map(([tema, cantidad]) => (
                                    <div key={tema} className="flex justify-between text-xs">
                                      <span className="text-gray-600">Tema {tema}:</span>
                                      <span className="text-orange-600">{cantidad}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {estadisticas.primeraFecha.distribucionPorTema && Object.keys(estadisticas.primeraFecha.distribucionPorTema).length > 0 && (
                                <div className="mt-3 pt-3 border-t border-blue-300">
                                  <div className="text-gray-700 font-medium mb-2">Distribución por Tema:</div>
                                  <div className="space-y-2">
                                    {Object.entries(estadisticas.primeraFecha.distribucionPorTema)
                                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                      .map(([tema, distribucion]) => (
                                        <div key={tema} className="bg-white rounded p-2 border border-blue-200">
                                          <div className="text-xs font-semibold text-blue-900 mb-1">Tema {tema}:</div>
                                          <div className="flex gap-2 text-xs">
                                            {distribucion.A > 0 && (
                                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                                A: {distribucion.A}
                                              </span>
                                            )}
                                            {distribucion.D > 0 && (
                                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">
                                                D: {distribucion.D}
                                              </span>
                                            )}
                                            {distribucion.DU > 0 && (
                                              <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded">
                                                DU: {distribucion.DU}
                                              </span>
                                            )}
                                            {distribucion.B > 0 && (
                                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                                                B: {distribucion.B}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Segunda Fecha */}
                    {estadisticas.segundaFecha && (
                      <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                        <h3 className="text-lg font-semibold text-purple-900 mb-3">
                          Segunda Fecha
                          {estadisticas.segundaFecha.descripcionFecha && (
                            <span className="text-sm font-normal text-purple-700 ml-2">
                              ({estadisticas.segundaFecha.descripcionFecha})
                            </span>
                          )}
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-700">Fecha:</span>
                            <span className="font-medium text-gray-900">
                              {new Date(estadisticas.segundaFecha.fecha).toLocaleDateString('es-AR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Total Notas:</span>
                            <span className="font-semibold text-gray-900">{estadisticas.segundaFecha.totalNotas}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Aprobados:</span>
                            <span className="font-semibold text-green-600">{estadisticas.segundaFecha.aprobados}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">Desaprobados:</span>
                            <span className="font-semibold text-red-600">{estadisticas.segundaFecha.desaprobados}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-700">% Aprobados:</span>
                            <span className="font-semibold text-gray-900">{estadisticas.segundaFecha.porcentajeAprobados}%</span>
                          </div>
                          {estadisticas.curso.tipoEvaluacion === 'nota_por_tema' && (
                            <div className="mt-3 pt-3 border-t border-purple-300">
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-700 font-medium">Temas en Blanco:</span>
                                <span className="font-semibold text-orange-600">{estadisticas.segundaFecha.temasEnBlanco}</span>
                              </div>
                              {Object.keys(estadisticas.segundaFecha.temasEnBlancoPorTema).length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {Object.entries(estadisticas.segundaFecha.temasEnBlancoPorTema).map(([tema, cantidad]) => (
                                    <div key={tema} className="flex justify-between text-xs">
                                      <span className="text-gray-600">Tema {tema}:</span>
                                      <span className="text-orange-600">{cantidad}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {estadisticas.segundaFecha.distribucionPorTema && Object.keys(estadisticas.segundaFecha.distribucionPorTema).length > 0 && (
                                <div className="mt-3 pt-3 border-t border-purple-300">
                                  <div className="text-gray-700 font-medium mb-2">Distribución por Tema:</div>
                                  <div className="space-y-2">
                                    {Object.entries(estadisticas.segundaFecha.distribucionPorTema)
                                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                      .map(([tema, distribucion]) => (
                                        <div key={tema} className="bg-white rounded p-2 border border-purple-200">
                                          <div className="text-xs font-semibold text-purple-900 mb-1">Tema {tema}:</div>
                                          <div className="flex gap-2 text-xs">
                                            {distribucion.A > 0 && (
                                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                                A: {distribucion.A}
                                              </span>
                                            )}
                                            {distribucion.D > 0 && (
                                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">
                                                D: {distribucion.D}
                                              </span>
                                            )}
                                            {distribucion.DU > 0 && (
                                              <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded">
                                                DU: {distribucion.DU}
                                              </span>
                                            )}
                                            {distribucion.B > 0 && (
                                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                                                B: {distribucion.B}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Fechas de Examen Configuradas */}
              {estadisticas.fechasExamen && estadisticas.fechasExamen.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Fechas de Examen Configuradas
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fecha
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Descripción
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estado
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {estadisticas.fechasExamen.map((fecha) => (
                          <tr key={fecha.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {new Date(fecha.fecha).toLocaleDateString('es-AR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {getTipoFechaLabel(fecha.tipo)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {fecha.descripcion || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                fecha.activa 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {fecha.activa ? 'Activa' : 'Inactiva'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Estadísticas detalladas por fecha */}
              {estadisticas.estadisticasPorFecha && estadisticas.estadisticasPorFecha.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Estadísticas Detalladas por Fecha
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fecha
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Notas
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Aprobados
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Desaprobados
                          </th>
                          {estadisticas.curso.tipoEvaluacion === 'nota_por_tema' && (
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Temas en Blanco
                            </th>
                          )}
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            % Aprobados
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Distribución
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {estadisticas.estadisticasPorFecha.map((estadistica, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {new Date(estadistica.fecha).toLocaleDateString('es-AR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {estadistica.tipoFecha ? getTipoFechaLabel(estadistica.tipoFecha) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                              {estadistica.totalNotas}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-green-600 font-semibold">
                              {estadistica.aprobados}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-red-600 font-semibold">
                              {estadistica.desaprobados}
                            </td>
                            {estadisticas.curso.tipoEvaluacion === 'nota_por_tema' && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-orange-600 font-semibold">
                                {estadistica.temasEnBlanco}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                              {estadistica.porcentajeAprobados}%
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-2 justify-center">
                                  {Object.entries(estadistica.distribucionNotas).map(([nota, cantidad]) => (
                                    cantidad > 0 && (
                                      <span key={nota} className="px-2 py-1 bg-gray-100 rounded text-xs">
                                        {nota}: {cantidad}
                                      </span>
                                    )
                                  ))}
                                </div>
                                {estadisticas.curso.tipoEvaluacion === 'nota_por_tema' && 
                                 estadistica.distribucionPorTema && 
                                 Object.keys(estadistica.distribucionPorTema).length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <div className="text-xs font-semibold text-gray-600 mb-1">Por Tema:</div>
                                    <div className="flex flex-wrap gap-1 justify-center">
                                      {Object.entries(estadistica.distribucionPorTema)
                                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                        .map(([tema, distribucion]) => (
                                          <div key={tema} className="text-xs">
                                            <span className="font-medium text-gray-700">T{tema}:</span>
                                            {distribucion.A > 0 && (
                                              <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                                                A{distribucion.A}
                                              </span>
                                            )}
                                            {distribucion.D > 0 && (
                                              <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                                                D{distribucion.D}
                                              </span>
                                            )}
                                            {distribucion.DU > 0 && (
                                              <span className="ml-1 px-1.5 py-0.5 bg-red-200 text-red-800 rounded">
                                                DU{distribucion.DU}
                                              </span>
                                            )}
                                            {distribucion.B > 0 && (
                                              <span className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                                                B{distribucion.B}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <p className="text-gray-600 text-center">No hay estadísticas disponibles para este curso.</p>
            </div>
          )}
        </>
      )}

      {!selectedCurso && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-gray-600 text-center">Selecciona un curso para ver el dashboard de estadísticas.</p>
        </div>
      )}
    </div>
  );
};

export default ListadoNotasPage;
